import React, { FormEvent } from 'react';
import {
  FolderPlus,
  Layers,
  Pencil,
  Plus,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  HsmContact,
  HsmContactBatch,
  HsmContactFilter,
  HsmContactSortKey,
  HsmCsvImportResult,
} from '../../types';
import { ContactMobileCard } from './ContactMobileCard';
import { ContactsTable } from './ContactsTable';
import { ContactFormDialog } from './ContactFormDialog';
import { CreateBatchDialog } from './CreateBatchDialog';
import { AssignBatchDialog } from './AssignBatchDialog';
import { CsvImportDialog } from './CsvImportDialog';
import { BatchEditDialog } from './BatchEditDialog';

export interface HsmContactsTabProps {
  // State
  contacts: HsmContact[];
  contactsLoading: boolean;
  contactsFilter: HsmContactFilter;
  setContactsFilter: (filter: HsmContactFilter) => void;
  contactsSearch: string;
  setContactsSearch: (search: string) => void;
  contactsSort: { column: HsmContactSortKey; direction: 'asc' | 'desc' };
  toggleContactsSort: (column: HsmContactSortKey) => void;
  contactDialogOpen: boolean;
  setContactDialogOpen: (open: boolean) => void;
  bulkImportDialogOpen: boolean;
  setBulkImportDialogOpen: (open: boolean) => void;
  selectedContact: HsmContact | null;

  // Form state
  contactPhoneNumber: string;
  setContactPhoneNumber: (value: string) => void;
  contactName: string;
  setContactName: (value: string) => void;
  contactCompany: string;
  setContactCompany: (value: string) => void;
  contactCpfCnpj: string;
  setContactCpfCnpj: (value: string) => void;
  contactEmail: string;
  setContactEmail: (value: string) => void;
  contactCampaignDay: string;
  setContactCampaignDay: (value: string) => void;
  contactBatchId: string;
  setContactBatchId: (value: string) => void;
  contactSaving: boolean;

  // Batch state
  batches: HsmContactBatch[];
  batchesLoading: boolean;
  selectedBatchFilter: number | null;
  setSelectedBatchFilter: (batchId: number | null) => void;
  batchDialogOpen: boolean;
  setBatchDialogOpen: (open: boolean) => void;
  batchName: string;
  setBatchName: (value: string) => void;
  batchDescription: string;
  setBatchDescription: (value: string) => void;
  batchSaving: boolean;
  assignBatchDialogOpen: boolean;
  setAssignBatchDialogOpen: (open: boolean) => void;
  contactToAssign: HsmContact | null;
  assignBatchId: string;
  setAssignBatchId: (value: string) => void;
  assigningBatch: boolean;

  // Batch edit state
  batchEditDialogOpen: boolean;
  setBatchEditDialogOpen: (open: boolean) => void;
  batchToEdit: HsmContactBatch | null;

  // Permissions
  isAdmin: boolean;

  // Handlers
  fetchContacts: () => Promise<{ success: boolean; error?: string }>;
  fetchBatches: () => Promise<{ success: boolean; error?: string }>;
  openNewContactDialog: () => void;
  openEditContactDialog: (contact: HsmContact) => void;
  handleCreateContact: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateContact: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteContact: (contactId: number) => Promise<void>;
  handleCreateBatch: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteBatch: (batchId: number) => Promise<void>;
  openAssignBatchDialog: (contact: HsmContact) => void;
  handleAssignBatch: () => Promise<void>;
  openBatchEditDialog: (batch: HsmContactBatch) => void;
  handleUpdateBatch: (
    batchId: number,
    name: string,
    description?: string
  ) => Promise<{ success: boolean; error?: string }>;
  handleCsvImport: (
    file: File,
    batchId?: number,
    batchName?: string
  ) => Promise<{ success: boolean; error?: string; result?: HsmCsvImportResult }>;
}

export function HsmContactsTab({
  contacts,
  contactsLoading,
  contactsFilter,
  setContactsFilter,
  contactsSearch,
  setContactsSearch,
  contactsSort,
  toggleContactsSort,
  contactDialogOpen,
  setContactDialogOpen,
  bulkImportDialogOpen,
  setBulkImportDialogOpen,
  selectedContact,
  contactPhoneNumber,
  setContactPhoneNumber,
  contactName,
  setContactName,
  contactCompany,
  setContactCompany,
  contactCpfCnpj,
  setContactCpfCnpj,
  contactEmail,
  setContactEmail,
  contactBatchId,
  setContactBatchId,
  contactSaving,
  batches,
  batchesLoading,
  selectedBatchFilter,
  setSelectedBatchFilter,
  batchDialogOpen,
  setBatchDialogOpen,
  batchName,
  setBatchName,
  batchDescription,
  setBatchDescription,
  batchSaving,
  assignBatchDialogOpen,
  setAssignBatchDialogOpen,
  contactToAssign,
  assignBatchId,
  setAssignBatchId,
  assigningBatch,
  batchEditDialogOpen,
  setBatchEditDialogOpen,
  batchToEdit,
  isAdmin,
  fetchContacts,
  fetchBatches,
  openNewContactDialog,
  openEditContactDialog,
  handleCreateContact,
  handleUpdateContact,
  handleDeleteContact,
  handleCreateBatch,
  openAssignBatchDialog,
  handleAssignBatch,
  openBatchEditDialog,
  handleUpdateBatch,
  handleCsvImport,
}: HsmContactsTabProps) {
  return (
    <>
      <div className="space-y-4">
        {/* Header: Search, Batch Filter, and Actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Input
              placeholder="Pesquisar telefone ou nome..."
              value={contactsSearch}
              onChange={(e) => setContactsSearch(e.target.value)}
              className="sm:max-w-xs"
            />
            {/* Batch Filter with Edit */}
            <div className="flex gap-1">
              <Select
                value={selectedBatchFilter?.toString() || 'all'}
                onValueChange={(value: string) =>
                  setSelectedBatchFilter(value === 'all' ? null : parseInt(value))
                }
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Layers className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filtrar por lote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os lotes</SelectItem>
                  {batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id.toString()}>
                      {batch.name} ({batch.contactCount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Edit selected batch button */}
              {selectedBatchFilter && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const batch = batches.find((b) => b.id === selectedBatchFilter);
                    if (batch) openBatchEditDialog(batch);
                  }}
                  title="Editar lote selecionado"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBatchDialogOpen(true)}>
              <FolderPlus className="mr-2 h-4 w-4" />
              Novo Lote
            </Button>
            <Button variant="outline" onClick={() => setBulkImportDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Importar
            </Button>
            <Button onClick={openNewContactDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Contato
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                fetchContacts();
                fetchBatches();
              }}
              disabled={contactsLoading}
            >
              <RefreshCw className={cn('h-4 w-4', contactsLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Contact Status Filter Tabs */}
        <Tabs
          value={contactsFilter}
          onValueChange={(v) => setContactsFilter(v as HsmContactFilter)}
        >
          <TabsList>
            <TabsTrigger value="all">Todos</TabsTrigger>
            <TabsTrigger value="active">Ativos</TabsTrigger>
            <TabsTrigger value="pending">Pendentes</TabsTrigger>
            <TabsTrigger value="completed">Concluidos</TabsTrigger>
            <TabsTrigger value="opted_out">Opt-out</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {contactsLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              Nenhum contato encontrado.
            </p>
          ) : (
            contacts.map((contact) => (
              <ContactMobileCard
                key={contact.id}
                contact={contact}
                isAdmin={isAdmin}
                onEdit={openEditContactDialog}
                onAssignBatch={openAssignBatchDialog}
                onDelete={handleDeleteContact}
              />
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <ContactsTable
          contacts={contacts}
          loading={contactsLoading}
          sortColumn={contactsSort.column}
          sortDirection={contactsSort.direction}
          isAdmin={isAdmin}
          onToggleSort={toggleContactsSort}
          onEdit={openEditContactDialog}
          onAssignBatch={openAssignBatchDialog}
          onDelete={handleDeleteContact}
        />
      </div>

      {/* Contact Form Dialog */}
      <ContactFormDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        selectedContact={selectedContact}
        batches={batches}
        saving={contactSaving}
        phoneNumber={contactPhoneNumber}
        setPhoneNumber={setContactPhoneNumber}
        name={contactName}
        setName={setContactName}
        company={contactCompany}
        setCompany={setContactCompany}
        cpfCnpj={contactCpfCnpj}
        setCpfCnpj={setContactCpfCnpj}
        email={contactEmail}
        setEmail={setContactEmail}
        batchId={contactBatchId}
        setBatchId={setContactBatchId}
        onSubmitCreate={handleCreateContact}
        onSubmitUpdate={handleUpdateContact}
      />

      {/* Create Batch Dialog */}
      <CreateBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        batchName={batchName}
        setBatchName={setBatchName}
        batchDescription={batchDescription}
        setBatchDescription={setBatchDescription}
        saving={batchSaving}
        onSubmit={handleCreateBatch}
      />

      {/* Assign Batch Dialog */}
      <AssignBatchDialog
        open={assignBatchDialogOpen}
        onOpenChange={setAssignBatchDialogOpen}
        contact={contactToAssign}
        batches={batches}
        selectedBatchId={assignBatchId}
        setSelectedBatchId={setAssignBatchId}
        saving={assigningBatch}
        onSave={handleAssignBatch}
      />

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={bulkImportDialogOpen}
        onOpenChange={setBulkImportDialogOpen}
        batches={batches}
        batchesLoading={batchesLoading}
        onImport={handleCsvImport}
        onRefresh={() => {
          fetchContacts();
          fetchBatches();
        }}
      />

      {/* Batch Edit Dialog */}
      <BatchEditDialog
        open={batchEditDialogOpen}
        onOpenChange={setBatchEditDialogOpen}
        batch={batchToEdit}
        onSave={handleUpdateBatch}
      />
    </>
  );
}
